<template>
  <Teleport to="body">
    <Transition name="fg-drawer-overlay">
      <div v-if="open" class="fg-drawer__backdrop" @click.self="emit('close')">
        <Transition :name="isMobile ? 'fg-drawer-sheet' : 'fg-drawer-side'" appear>
          <aside
            v-if="open"
            class="fg-drawer"
            :class="{ 'fg-drawer--sheet': isMobile }"
            role="dialog"
            aria-modal="true"
            :aria-label="title"
            @click.stop
          >
            <header class="fg-drawer__header">
              <button
                v-if="showBack"
                type="button"
                class="fg-btn fg-btn--icon"
                :aria-label="t('common.back')"
                @click="emit('back')"
              ><Icon name="arrow-left" /></button>
              <div class="fg-drawer__title-wrap">
                <h2 class="fg-drawer__title">{{ title }}</h2>
                <p v-if="subtitle" class="fg-drawer__subtitle">{{ subtitle }}</p>
              </div>
              <button
                type="button"
                class="fg-btn fg-btn--icon"
                :aria-label="t('common.close')"
                @click="emit('close')"
              ><Icon name="x" /></button>
            </header>

            <div class="fg-drawer__body">
              <slot />
            </div>

            <footer v-if="$slots.footer" class="fg-drawer__footer">
              <slot name="footer" />
            </footer>
          </aside>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from '../i18n'
import { lockScroll, unlockScroll } from '../utils/scroll-lock'
import Icon from './Icon.vue'

const MOBILE_BREAKPOINT_PX = 600

const props = defineProps<{
  open: boolean
  title: string
  subtitle?: string
  showBack?: boolean
}>()
const emit = defineEmits<{ close: []; back: [] }>()

const { t } = useI18n()
const containerWidth = ref<number>(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => containerWidth.value < MOBILE_BREAKPOINT_PX)

let cleanupResize: (() => void) | null = null
let cleanupEsc: (() => void) | null = null
let locked = false

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    // Verrouille le scroll de la page hôte tant que le drawer est ouvert.
    if (!locked) { lockScroll(); locked = true }
    if (!cleanupResize) {
      const onResize = () => { containerWidth.value = window.innerWidth }
      window.addEventListener('resize', onResize, { passive: true })
      cleanupResize = () => window.removeEventListener('resize', onResize)
    }
    if (!cleanupEsc) {
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') emit('close') }
      window.addEventListener('keydown', onKey)
      cleanupEsc = () => window.removeEventListener('keydown', onKey)
    }
  } else if (locked) {
    unlockScroll()
    locked = false
  }
}, { immediate: true })

onBeforeUnmount(() => {
  cleanupResize?.()
  cleanupEsc?.()
  if (locked) { unlockScroll(); locked = false }
})
</script>
